/**
 * Timbrica — RIFE shared math (frame interpolation, browser).
 *
 * Pure functions shared by the inference worker (`rife-worker.js`), the page
 * that prices a run before it starts (`rife-core.js` → framePlan/frameCostMs)
 * and the unit tests (`tests/js/rife-shared.test.mjs`). No DOM, no ORT, no state:
 * everything here is a plain transform on typed arrays, which is what makes
 * the tile stitch and the padding testable in node — the black 1-px border
 * of 2026-09-12 lived exactly here and was found by a measurement, not a
 * test, because the old code could only run inside a page.
 *
 * Contract (all images are RGBA Uint8ClampedArray of width×height×4):
 *   MODEL_SCHEMAS         — how a RIFE ONNX export wants its inputs
 *   schemaFor(inputNames, inputDims) → 'three' | 'seven' | 'six'
 *   alignFor(schema)      — spatial alignment the export requires (32 / 64)
 *   rgbaToCHW(rgba, w, h, pw, ph) → Float32Array [3, ph, pw], edge-replicated
 *   chwToRGBA(chw, pw, ph, w, h) → Uint8ClampedArray [h, w, 4] cropped
 *   planTiles(w, h, tileSize, overlap) → [{x, y, w, h}]
 *   framePlan(w, h, tileSize, overlap, align) → { whole, tiles, ts, ov, area }
 *   frameCostMs(w, h, tileSize, overlap, align, refMs, refW, refH) → ms per interpolated frame
 *   edgeWeights(len, overlap, lowEdge, highEdge) → Float32Array
 *   cropRGBA(rgba, srcW, x, y, w, h) → Uint8ClampedArray
 *   createStitcher(w, h) → { add(tileRGBA, tile, overlap), finish() }
 */
(function (root) {
    'use strict';

    var MODEL_SCHEMAS = {
        // practical-rife / yuvraj108c exports: img0 [1,3,H,W], img1 [1,3,H,W], timestep [1]
        three: { inputs: 3, channels: 3, align: 32, timestep: 'scalar' },
        // vs-mlrt "rife_v2" exports (v4.22–v4.26): input [1,7,H,W] = img0 ‖ img1 ‖ t-plane
        seven: { inputs: 1, channels: 7, align: 64, timestep: 'plane' },
        // legacy concat exports: imgs [1,6,H,W], fixed midpoint
        six:   { inputs: 1, channels: 6, align: 32, timestep: 'none' }
    };

    function schemaFor(inputNames, firstInputDims) {
        var n = (inputNames || []).length;
        if (n >= 3) return 'three';
        var c = firstInputDims && firstInputDims[1];
        if (c === 7) return 'seven';
        if (c === 6) return 'six';
        return n === 1 ? 'seven' : 'three';
    }

    function alignFor(schema) {
        var s = MODEL_SCHEMAS[schema];
        return s ? s.align : 32;
    }

    function padTo(v, align) { return Math.ceil(v / align) * align; }

    // RGBA → planar RGB in [0,1], edge-replicated to (pw × ph).
    function rgbaToCHW(rgba, w, h, pw, ph) {
        var out = new Float32Array(3 * pw * ph);
        var plane = pw * ph;
        for (var y = 0; y < ph; y++) {
            var sy = y < h ? y : h - 1;
            var rowBase = sy * w;
            var dst = y * pw;
            for (var x = 0; x < pw; x++) {
                var sx = x < w ? x : w - 1;
                var i = (rowBase + sx) * 4;
                out[dst + x] = rgba[i] / 255;
                out[plane + dst + x] = rgba[i + 1] / 255;
                out[2 * plane + dst + x] = rgba[i + 2] / 255;
            }
        }
        return out;
    }

    // Planar RGB [3, ph, pw] in [0,1] → RGBA bytes of the top-left w × h.
    // (Uint8ClampedArray rounds to nearest on assignment — no +0.5: it turned a
    // float-error 128.00001 into 129 in the stitcher's normalisation.)
    function chwToRGBA(chw, pw, ph, w, h) {
        var out = new Uint8ClampedArray(w * h * 4);
        var plane = pw * ph;
        for (var y = 0; y < h; y++) {
            var src = y * pw, dst = y * w * 4;
            for (var x = 0; x < w; x++) {
                var s = src + x, d = dst + x * 4;
                out[d]     = chw[s] * 255;
                out[d + 1] = chw[plane + s] * 255;
                out[d + 2] = chw[2 * plane + s] * 255;
                out[d + 3] = 255;
            }
        }
        return out;
    }

    // Tile plan in input coordinates: rows of tiles, the last one in each
    // direction pulled back so it ends on the frame edge (so tiles overlap by
    // MORE than `overlap` there, which the stitcher's normalisation absorbs).
    function planTiles(width, height, tileSize, overlap) {
        var stride = tileSize - overlap;
        var tiles = [];
        for (var y = 0; y < height; y += stride) {
            var ty = Math.min(y, Math.max(0, height - tileSize));
            for (var x = 0; x < width; x += stride) {
                var tx = Math.min(x, Math.max(0, width - tileSize));
                tiles.push({ x: tx, y: ty, w: Math.min(tileSize, width - tx), h: Math.min(tileSize, height - ty) });
                if (tx + tileSize >= width) break;
            }
            if (ty + tileSize >= height) break;
        }
        return tiles;
    }

    // The plan inferFrame() executes for a w×h frame: one inference on the whole
    // frame when it fits the tile budget, else overlapping tiles of `ts`.
    // ONE definition for the worker (which runs it) and the page (which prices
    // it, 2026-09-26): a second copy of this arithmetic is how an estimate
    // drifts away from the run it describes. `area` is the padded pixel count
    // of ONE inference — what an inference costs scales with it.
    function framePlan(w, h, tileSize, overlap, align) {
        align = align || 32;
        if (Math.max(w, h) <= tileSize) {
            return { whole: true, tiles: 1, ts: 0, ov: 0, area: padTo(w, align) * padTo(h, align) };
        }
        var ts = Math.min(tileSize, w, h);
        if (ts % align !== 0) ts -= ts % align;
        if (ts < align * 2) ts = align * 2;
        var ov = Math.min(overlap, Math.floor(ts / 4));
        return { whole: false, tiles: planTiles(w, h, ts, ov).length, ts: ts, ov: ov, area: padTo(ts, align) * padTo(ts, align) };
    }

    // Milliseconds ONE interpolated frame of w×h costs, priced from a measured
    // inference of `refW×refH` (the known-answer test the provider ladder
    // already runs): inferences in the plan × the reference cost scaled by
    // padded area. Checked against 136 finished AI runs on prod (2026-09-26):
    // together with the run-size test it lands within ×2 of the real run time
    // in 82 % (desktop) and 93 % (phones) of runs — where the constant it
    // replaces was 6× and 26× off in the median.
    function frameCostMs(w, h, tileSize, overlap, align, refMs, refW, refH) {
        if (!(refMs > 0) || !(w > 0) || !(h > 0)) return 0;
        var p = framePlan(w, h, tileSize, overlap, align);
        var refArea = padTo(refW || 384, align || 32) * padTo(refH || refW || 384, align || 32);
        return p.tiles * refMs * (p.area / refArea);
    }

    // Feather weights along one axis of a tile: a cosine ramp over `overlap`
    // samples at each end — EXCEPT at an end that is the frame's own border,
    // where there is no neighbour to blend with and the weight stays 1.
    // ⚠️ Until 2026-09-12 the ramp came from one mask shared by every tile and
    // reached exactly 0 at the tile's first and last sample even on the frame
    // border; `weight || 1` then turned that pixel into 0 — a black 1-px frame
    // around every tiled result and a ceiling of ~31 dB on its PSNR (tiled RIFE
    // scored like a crossfade: 31.7 dB against 36.7 whole-frame; after this
    // function, 36.7 for both). The ramp is offset by half a sample so a tile
    // never writes a pixel with weight 0.
    function edgeWeights(len, overlap, lowEdge, highEdge) {
        var m = new Float32Array(len);
        for (var i = 0; i < len; i++) {
            var d = Math.min(lowEdge ? Infinity : i, highEdge ? Infinity : len - 1 - i);
            m[i] = d >= overlap ? 1 : 0.5 - 0.5 * Math.cos((d + 0.5) / overlap * Math.PI);
        }
        return m;
    }

    function cropRGBA(rgba, srcW, x, y, w, h) {
        var out = new Uint8ClampedArray(w * h * 4);
        var rowBytes = srcW * 4;
        for (var py = 0; py < h; py++) {
            var s = (y + py) * rowBytes + x * 4;
            out.set(rgba.subarray(s, s + w * 4), py * w * 4);
        }
        return out;
    }

    // Feathered accumulator for tiled outputs. add() takes a tile's RGBA
    // result and its rectangle; finish() normalises and returns RGBA.
    function createStitcher(w, h) {
        var accum = new Float32Array(w * h * 3);
        var weight = new Float32Array(w * h);
        return {
            add: function (tileRGBA, tile, overlap) {
                var maskX = edgeWeights(tile.w, overlap, tile.x === 0, tile.x + tile.w >= w);
                var maskY = edgeWeights(tile.h, overlap, tile.y === 0, tile.y + tile.h >= h);
                for (var py = 0; py < tile.h; py++) {
                    var dstY = tile.y + py;
                    if (dstY >= h) break;
                    var ay = maskY[py];
                    for (var px = 0; px < tile.w; px++) {
                        var dstX = tile.x + px;
                        if (dstX >= w) break;
                        var a = ay * maskX[px];
                        var d = dstY * w + dstX, s = (py * tile.w + px) * 4;
                        accum[d * 3]     += tileRGBA[s] * a;
                        accum[d * 3 + 1] += tileRGBA[s + 1] * a;
                        accum[d * 3 + 2] += tileRGBA[s + 2] * a;
                        weight[d] += a;
                    }
                }
            },
            finish: function () {
                var out = new Uint8ClampedArray(w * h * 4);
                for (var p = 0; p < w * h; p++) {
                    var wt = weight[p] || 1, i = p * 4;
                    out[i]     = accum[p * 3] / wt;
                    out[i + 1] = accum[p * 3 + 1] / wt;
                    out[i + 2] = accum[p * 3 + 2] / wt;
                    out[i + 3] = 255;
                }
                return out;
            }
        };
    }

    // ─── IEEE 754 half-float conversion (for fp16 exports) ─────────────
    var _f32 = new Float32Array(1);
    var _i32 = new Int32Array(_f32.buffer);
    function f32ToF16(val) {
        _f32[0] = val;
        var x = _i32[0];
        var bits = (x >> 16) & 0x8000;
        var m = (x >> 12) & 0x07ff;
        var e = (x >> 23) & 0xff;
        if (e < 103) return bits;
        if (e > 142) { bits |= 0x7c00; bits |= ((e === 255) ? 0 : 1) && (x & 0x007fffff); return bits; }
        if (e < 113) { m |= 0x0800; bits |= (m >> (114 - e)) + ((m >> (113 - e)) & 1); return bits; }
        bits |= ((e - 112) << 10) | (m >> 1);
        bits += m & 1;
        return bits;
    }
    function f16ToF32(h) {
        var s = (h & 0x8000) >> 15, e = (h & 0x7c00) >> 10, f = h & 0x03ff;
        if (e === 0) return (s ? -1 : 1) * Math.pow(2, -14) * (f / 1024);
        if (e === 0x1f) return f ? NaN : ((s ? -1 : 1) * Infinity);
        return (s ? -1 : 1) * Math.pow(2, e - 15) * (1 + (f / 1024));
    }
    function f32ArrayToF16(src) { var d = new Uint16Array(src.length); for (var i = 0; i < src.length; i++) d[i] = f32ToF16(src[i]); return d; }
    function f16ArrayToF32(src) { var d = new Float32Array(src.length); for (var i = 0; i < src.length; i++) d[i] = f16ToF32(src[i]); return d; }

    root.RifeShared = {
        MODEL_SCHEMAS: MODEL_SCHEMAS,
        schemaFor: schemaFor,
        alignFor: alignFor,
        padTo: padTo,
        rgbaToCHW: rgbaToCHW,
        chwToRGBA: chwToRGBA,
        planTiles: planTiles,
        framePlan: framePlan,
        frameCostMs: frameCostMs,
        edgeWeights: edgeWeights,
        cropRGBA: cropRGBA,
        createStitcher: createStitcher,
        f32ArrayToF16: f32ArrayToF16,
        f16ArrayToF32: f16ArrayToF32
    };
})(typeof self !== 'undefined' ? self : globalThis);
